import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

/**
 * ForwardModalComponent
 *
 * Standalone child component that renders the "Forward Message" modal.
 * The parent (MainUnifiedChatComponent) passes in the already-filtered
 * chat list and handles the actual `sendForward(chat)` logic via the
 * (forward) output event.
 */
@Component({
  selector: 'app-forward-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  styleUrls: ['./forward-modal-component.css'],
  template: `
    <div class="forward-modal-overlay" [class.dark-theme]="isDarkMode" (click)="close.emit()">
      <div class="forward-modal" [class.dark-theme]="isDarkMode" (click)="$event.stopPropagation()">

        <!-- Header -->
        <div class="forward-modal-header">
          <h5 class="forward-modal-title">Forward Message</h5>
          <button class="forward-close-btn" (click)="close.emit()">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
              viewBox="0 0 24 24" fill="none" stroke="currentColor"
              stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <!-- Search -->
        <div class="forward-search-container">
          <div class="forward-search-wrapper">
            <svg class="forward-search-icon" xmlns="http://www.w3.org/2000/svg"
              width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              [(ngModel)]="searchTerm"
              placeholder="Search people or groups..."
              class="forward-search-input"
            />
          </div>
        </div>

        <!-- Chat List Body -->
        <div class="forward-modal-body">
          <div class="forward-chat-list">

            @for (chat of filteredChats; track chat.channelId || chat.userId) {
              <div class="forward-chat-item" (click)="onSendForward(chat)">
                <div class="forward-chat-info">

                  <!-- Avatar -->
                  <div class="forward-avatar-wrapper">
                    @if (chat.chatType === 'group') {
                      @if (chat.groupImg || chat.groupImage) {
                        <img
                          [src]="chat.groupImg || chat.groupImage"
                          class="forward-avatar"
                          (error)="handleImageError($event)"
                          alt="Group"
                        />
                      } @else {
                        <div class="forward-avatar-placeholder group">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                            <circle cx="9" cy="7" r="4"></circle>
                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                            <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                          </svg>
                        </div>
                      }
                    } @else {
                      @if (chat.profileImage) {
                        <img
                          [src]="chat.profileImage"
                          class="forward-avatar"
                          (error)="handleImageError($event)"
                          alt="User"
                        />
                      } @else {
                        <div class="forward-avatar-placeholder user">
                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                            viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                          </svg>
                        </div>
                      }
                    }
                  </div>

                  <!-- Chat Details -->
                  <div class="forward-chat-details">
                    <span class="forward-chat-name">
                      {{ chat.displayName || chat.userName || chat.groupName }}
                    </span>
                    @if (chat.chatType === 'group') {
                      <small class="forward-chat-type">
                        Group &bull; {{ chat.members?.length || 0 }} members
                      </small>
                    } @else {
                      <small class="forward-chat-type">Personal chat</small>
                    }
                  </div>
                </div>

                <!-- Send Button -->
                <button
                  class="forward-send-btn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="22" y1="2" x2="11" y2="13"></line>
                    <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                  </svg>
                </button>
              </div>
            }

            <!-- Empty State -->
            @if (filteredChats.length === 0) {
              <div class="forward-empty-state">
                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <p class="forward-empty-text">No chats found</p>
                <small class="forward-empty-hint">Try a different search term</small>
              </div>
            }

          </div>
        </div>

      </div>
    </div>
  `
})
export class ForwardModalComponent {
  @Input() visible = false;
  @Input() chats: any[] = [];
  @Input() message: string | null = null;
  @Input() files: { fileUrl: string; name?: string }[] = [];
  @Input() meta: any = null;
  @Input() currentChatId: string | null = null;
  @Input() userId: string | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() forward = new EventEmitter<any>();
  searchTerm = '';

  get isDarkMode(): boolean {
    return typeof document !== 'undefined' && (
      document.body.classList.contains('dark-theme') ||
      document.documentElement.classList.contains('dark-theme')
    );
  }
  get filteredChats(): any[] {
    if (!this.searchTerm.trim()) {
      return this.chats;
    }
    const term = this.searchTerm.toLowerCase();
    return this.chats.filter((c: any) => {
      const name = c.displayName || c.userName || c.groupName || '';
      return name.toLowerCase().includes(term);
    });
  }
  onSendForward(chat: any): void {
    this.forward.emit(chat);
  }
  handleImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = 'assets/user-icon.png';
  }
}
